import type { Step } from 'react-joyride';

/**
 * HOW TO ADD A PAGE TOUR
 * ----------------------
 * 1. Put data-tour="some-id" on the element you want to highlight.
 * 2. Add a Step below with target: '[data-tour="some-id"]'.
 * 3. Register those steps under the route path in tourStepsByPath.
 * 4. Open that page and click the "?" button in the navbar.
 */

/** Shared chrome available on most authenticated pages. */
export const shellTourSteps: Step[] = [
  {
    target: '[data-tour="sidebar-nav"]',
    title: 'Navigation',
    content: 'Use the sidebar to move between Dashboard, Books, and other sections.',
    skipBeacon: true,
    placement: 'right',
  },
  {
    target: '[data-tour="nav-dashboard"]',
    title: 'Dashboard',
    content: 'This overview shows balances, key figures, and the record filter for your books.',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-records"]',
    title: 'Books',
    content: 'Create or open a set of books. Each one has its own transactions and accounts.',
    placement: 'right',
  },
  {
    target: '[data-tour="nav-ai"]',
    title: 'AI Assistant',
    content: 'Open Balancio AI from the header whenever you need help with your books.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="fab-ai"]',
    title: 'AI assistant',
    content: 'This button opens the AI assistant from any page.',
    placement: 'left',
  },
];

/** /dashboard */
export const dashboardTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="dashboard-record-select"]',
    title: 'Choose a record',
    content: 'Filter the overview to one set of books, or keep All Records to see combined totals.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-balances"]',
    title: 'Balances at a glance',
    content: 'Capital, Assets, and Liabilities update from your transactions for the selected record.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="dashboard-kpis"]',
    title: 'Key figures',
    content: 'Each card shows a trend from your ledger history for the selected books.',
    placement: 'top',
  },
];

/** /records */
export const recordsTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="records-new"]',
    title: 'Create books',
    content: 'Create a new set of books for a business, personal use, or a client.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="records-search"]',
    title: 'Search books',
    content: 'Search by name when you have more than one set of books.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="records-list"]',
    title: 'Your books',
    content: 'Open a card to view its transactions. Use the menu to edit, share, import, export, or manage access.',
    placement: 'top',
  },
];

/** /records/transactions */
export const transactionsTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="nav-transactions"]',
    title: 'Transactions section',
    content: 'After you select a record, use this sidebar link anytime to return to its transactions.',
    placement: 'right',
  },
  {
    target: '[data-tour="transactions-new"]',
    title: 'New transaction',
    content: 'Record a sale, expense, or other movement for these books. You can also describe it in plain language with AI.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="transactions-filters"]',
    title: 'Filters',
    content: 'Narrow by date, entity, amount, or who created the transaction.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="transactions-list"]',
    title: 'View transactions',
    content: 'Click a row to open details. Edit or delete from the row actions or the detail panel.',
    placement: 'top',
  },
];

/** /records/accounts */
export const accountsTourSteps: Step[] = [
  ...shellTourSteps,
  {
    target: '[data-tour="nav-accounts"]',
    title: 'Accounts section',
    content: 'Open ledgers, stock valuation, trading accounts, P&L, and balance sheets from here.',
    placement: 'right',
  },
  {
    target: '[data-tour="accounts-grid"]',
    title: 'Account books',
    content: 'Pick Ledgers for individual accounts, or open financial statements like Profit & Loss and Balance Sheets.',
    placement: 'top',
  },
  {
    target: '[data-tour="accounts-ledgers"]',
    title: 'Ledgers',
    content: 'View and manage all ledger accounts for the selected record.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="accounts-pnl"]',
    title: 'Profit & Loss',
    content: 'Open your P&L statement for this record.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="accounts-balance-sheet"]',
    title: 'Balance Sheets',
    content: 'View the balance sheet for assets, liabilities, and capital.',
    placement: 'bottom',
  },
];

/**
 * Map route pathnames → Joyride steps.
 */
const tourStepsByPath: Record<string, Step[]> = {
  '/dashboard': dashboardTourSteps,
  '/records': recordsTourSteps,
  '/records/transactions': transactionsTourSteps,
  '/records/accounts': accountsTourSteps,
};

/** Exact path match, then longest parent prefix, then shell fallback. */
export function getTourStepsForPath(pathname: string): Step[] {
  if (tourStepsByPath[pathname]) {
    return tourStepsByPath[pathname];
  }

  const parent = Object.keys(tourStepsByPath)
    .filter((path) => pathname.startsWith(`${path}/`))
    .sort((a, b) => b.length - a.length)[0];

  if (parent) {
    return tourStepsByPath[parent];
  }

  return shellTourSteps;
}

/** Short first-run tour: teach the first transaction, not the chrome. */
export const firstRunTourSteps: Step[] = [
  {
    target: '[data-tour="txn-examples"]',
    title: 'Example transactions',
    content: 'Select an example to fill the form. Edit the amounts afterwards so they match your figures.',
    skipBeacon: true,
    placement: 'bottom',
  },
  {
    target: '[data-tour="txn-form"]',
    title: 'Transaction details',
    content: 'Enter what happened, the item, the payment method, and the amount. Use + beside a field to add a new term.',
    placement: 'top',
  },
  {
    target: '[data-tour="txn-save"]',
    title: 'Save transaction',
    content: 'Save this entry to add it to your books. Related balances, such as Cash or Bank, will update.',
    placement: 'top',
    isFixed: true,
    hideOverlay: true,
    overlayClickAction: false,
  } as Step,
];

export function getFirstRunStepsForPath(pathname: string): Step[] {
  if (pathname.startsWith('/records/transactions')) {
    return firstRunTourSteps;
  }
  return firstRunTourSteps;
}
